import { memo, useCallback } from 'react';
import { Dropdown } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { IMPORT_TARGETS } from '../../constants';
import MappingTargetOption from './MappingTargetOption.jsx';

/**
 * "Imports as" dropdown of one file column. Each target belongs to one column only, so a
 * target used elsewhere is offered disabled with the name of its column.
 *
 * @param {object} props
 * @param {string} props.column Header of the file column.
 * @param {string} props.target Current target id (`'skip'` = Don't import).
 * @param {Record<string, string|null>} props.owners Column using each target, or `null`.
 * @param {(column: string, target: string) => void} props.onChange
 */
function MappingTargetSelect({ column, target, owners, onChange }) {
  const { t } = useTranslation('people-access');
  const handleSelect = useCallback((next) => onChange(column, next), [onChange, column]);
  const current = IMPORT_TARGETS.find(({ id }) => id === target) ?? IMPORT_TARGETS.at(-1);

  return (
    <Dropdown
      label={t(current.labelKey)}
      variant="secondary"
      size="small"
      usePortal
      menuClassName="scrollbar-aimsos-auto-sm"
    >
      {IMPORT_TARGETS.map(({ id, labelKey }) => {
        const owner = owners[id];
        const taken = owner && owner !== column;
        return (
          <MappingTargetOption
            key={id}
            target={id}
            label={t(labelKey)}
            description={taken ? t('import.match.usedBy', { column: owner }) : undefined}
            selected={id === target}
            onSelect={handleSelect}
          />
        );
      })}
    </Dropdown>
  );
}

export default memo(MappingTargetSelect);
